<template>
    <div
        class="sbs-file-previewer"
        :class="previewerClasses">
        <!-- @slot Слот перед контентом -->
        <slot name="prepend">
            <SbsFilePreviewLoader
                v-bind="filePreviewLoaderData"
                @click="fileButtonClickHandler" />
        </slot>

        <!-- @slot Слот по-умолчанию -->
        <slot>
            <div class="sbs-file-previewer__file-info">
                <span
                    v-if="fileName"
                    class="sbs-file-previewer__file-name"
                    :class="fileNameClasses">
                    {{ fileName }}
                </span>

                <span
                    class="sbs-file-previewer__file-property"
                    :class="fileInfoClasses">
                    <span
                        v-if="fileExtension"
                        class="sbs-file-previewer__file-extension">{{ fileExtension }},</span>
                    <span v-if="fileSize">{{ fileSize }}</span>
                </span>
            </div>
        </slot>

        <!-- @slot Слот после контента -->
        <slot name="append">
            <SbsButtonIcon
                v-if="downloadable"
                class="sbs-file-previewer__close-icon"
                v-bind="closeButtonData"
                @click="removeFile" />
        </slot>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';
import {
    getFileName,
    getFileExtension,
} from '../../utils/fileTypes.js';
import { formatBytes } from '../../utils/helpers.js';

const COMPLETED_PROGRESS = 100;

export default {
    name: 'SbsFilePreviewer',
    mixins: [themesMixin],
    computed: {
        isLoading() {
            return (
                this.progress > 0 &&
                this.progress < COMPLETED_PROGRESS
            );
        },
        isLoaded() {
            return this.progress >= COMPLETED_PROGRESS;
        },
        previewerClasses() {
            return [
                {
                    loading: this.isLoading,
                    loaded: this.isLoaded,
                    disabled: this.disabled,
                },
            ];
        },
        fileName() {
            const fileName = this.file?.name ?? '';

            return getFileName(fileName);
        },
        fileSize() {
            const fileSize = this.file?.size ?? 0;

            return formatBytes(fileSize);
        },
        fileExtension() {
            const fileName = (this.file?.name ?? '').toLowerCase();

            return getFileExtension(fileName);
        },
        fileNameClasses() {
            return this.fileNameTheme
                ? `clr-${this.fileNameTheme}`
                : '';
        },
        fileInfoClasses() {
            return this.fileInfoTheme
                ? `clr-${this.fileInfoTheme}`
                : '';
        },
        closeButtonData() {
            return {
                disabled: this.disabled,
                iconName: 'close',
                colorTheme: AllColors.GRAY,
                hoverColor: AllColors.BLACK,
            };
        },
        filePreviewLoaderData() {
            return {
                cancelIconTheme: this.cancelIconTheme,
                disabled: this.disabled,
                downloadable: this.downloadable,
                downloadIconTheme: this.downloadIconTheme,
                fileBackgroundTheme: this.fileBackgroundTheme,
                fileHoverBackground: this.fileHoverBackground,
                fileHref: this.fileHref,
                fileIcon: this.fileIcon,
                fileIconTheme: this.fileIconTheme,
                loaderBorderRadius: this.loaderBorderRadius,
                loaderStrokeTheme: this.loaderStrokeTheme,
                loaderStrokeWidth: this.loaderStrokeWidth,
                progress: this.progress,
            };
        },
    },
    methods: {
        removeFile() {
            this.$emit('removeFile');
        },
        fileButtonClickHandler(e) {
            if (this.isLoaded && this.downloadable) return;

            e.preventDefault();
            this.removeFile();
        },
    },
    props: {
        /**
         * Цвет иконки удаления файла
         */
        cancelIconTheme: {
            type: String,
            default: AllColors.GRAY,
        },
        /**
         * Отключает любые действия с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность скачивания
         */
        downloadable: {
            type: Boolean,
            default: false,
        },
        /**
         * Цвет иконки загрузки/скачивания
         */
        downloadIconTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Объект загруженного файла
         */
        file: {
            type: [Object, File],
            default: () => ({
                name: '',
                type: '',
                size: '',
            }),
        },
        /**
         * Цвет фона под иконкой файла
         */
        fileBackgroundTheme: {
            type: String,
            default: AllColors.GRADIENT_PALE_BLUE,
        },
        /**
         * Цвет фона иконки при наведении
         */
        fileHoverBackground: {
            type: String,
            default: AllColors.GRAY_ULTRA_LIGHT,
        },
        /**
         * Ссылка на файл
         */
        fileHref: {
            type: String,
            default: '',
        },
        /**
         * Иконка файла
         */
        fileIcon: {
            type: String,
            default: 'file',
        },
        /**
         * Цвет иконки файла
         */
        fileIconTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Цвет текста информации о файле
         */
        fileInfoTheme: {
            type: String,
            default: AllColors.GRAY_DEEP,
        },
        /**
         * Цвет текста имени файла
         */
        fileNameTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Радиус скругления
         */
        loaderBorderRadius: {
            type: Number,
            default: 10,
        },
        /**
         * Цвет полосы загрузки
         */
        loaderStrokeTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Толщина полосы загрузки
         */
        loaderStrokeWidth: {
            type: Number,
            default: 2,
        },
        /**
         * Прогресс загрузки файла
         */
        progress: {
            type: Number,
            default: 0,
        },
    },
};
</script>

<style lang="scss">
.sbs-file-previewer {
    position: relative;
    display: flex;
    align-items: center;

    &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &__file-info {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        width: 0;
        max-width: 100%;
        padding-right: 10px;
        padding-left: 10px;
        opacity: 0.5;
        transition: $trs;

        .sbs-file-previewer.loaded & {
            opacity: 1;
        }
    }

    &__file-name {
        @include text-clamp;
        @include typo-subprimary-book;
    }

    &__file-extension {
        text-transform: uppercase;
    }

    &__file-property {
        @include typo-additional-book;
    }

    &__close-icon {
        align-self: flex-start;
        margin-left: auto;
    }
}
</style>
